import type { Force } from 'd3-force'
import type { FlowNode, VisualizationConfig } from '../types/market'
import { clamp } from '../utils/math'

export interface DynamicCollisionForce extends Force<FlowNode, undefined> {
  effectiveRadius(node: FlowNode): number
}

export const PHYSICS_RADIUS_SCALE = 1.01

/**
 * Collision only arranges bubbles; it never outranks a direction crossing.
 * With fewer than 50 nodes, a deterministic pair loop is both simpler and
 * cheaper than maintaining a quadtree with state-dependent radii.
 */
export function createDynamicCollisionForce(
  config: VisualizationConfig,
  iterations = 3,
): DynamicCollisionForce {
  let nodes: FlowNode[] = []

  const effectiveRadius = (node: FlowNode): number => {
    const scale = clamp(node.collisionScale, 0.22, 1)
    const padding = config.collisionPadding * (node.isCrossing ? 0.32 : scale)
    // node.radius is the data-driven base radius. Renderer breathing, burst
    // and glow scales are local draw values and never enter this calculation.
    return node.radius * PHYSICS_RADIUS_SCALE * scale + padding
  }

  const pairStrength = (left: FlowNode, right: FlowNode): number => {
    if (left.crossingArrived || right.crossingArrived) return 0.92
    if (left.isCrossing && right.isCrossing) return 0.16
    if (left.isCrossing || right.isCrossing) {
      const crossing = left.isCrossing ? left : right
      const stable = crossing === left ? right : left
      // A bubble leaving the stable bubble's region receives the clearest path.
      return crossing.targetSign !== stable.targetSign ? 0.16 : 0.28
    }
    // Stable bubbles in opposite, already-correct regions cannot overlap across
    // the axis, so they should not create an invisible wall for future crossings.
    if (left.targetSign !== right.targetSign) return 0.06
    return 0.98
  }

  const force = (() => {
    for (let iteration = 0; iteration < iterations; iteration += 1) {
      for (let leftIndex = 0; leftIndex < nodes.length; leftIndex += 1) {
        const left = nodes[leftIndex]
        const leftX = (left.x ?? 0) + (left.vx ?? 0)
        const leftY = (left.y ?? 0) + (left.vy ?? 0)
        const leftRadius = left.collisionRadius

        for (let rightIndex = leftIndex + 1; rightIndex < nodes.length; rightIndex += 1) {
          const right = nodes[rightIndex]
          const rightRadius = right.collisionRadius
          const minimumDistance = leftRadius + rightRadius
          let dx = ((right.x ?? 0) + (right.vx ?? 0)) - leftX
          let dy = ((right.y ?? 0) + (right.vy ?? 0)) - leftY
          let distanceSquared = dx * dx + dy * dy
          if (distanceSquared >= minimumDistance * minimumDistance) continue

          if (distanceSquared < 1e-8) {
            const angle = ((left.seed ^ right.seed) % 360) * Math.PI / 180
            dx = Math.cos(angle) * 0.01
            dy = Math.sin(angle) * 0.01
            distanceSquared = dx * dx + dy * dy
          }
          const distance = Math.sqrt(distanceSquared)
          const unitX = dx / distance
          const unitY = dy / distance
          const penetration = minimumDistance - distance
          const strength = pairStrength(left, right)
          if (strength <= 0) continue

          let leftShare: number
          let rightShare: number
          if (left.isCrossing !== right.isCrossing) {
            // Stable neighbours yield; the crossing bubble keeps directional momentum.
            leftShare = left.isCrossing ? 0.16 : 0.84
            rightShare = right.isCrossing ? 0.16 : 0.84
          } else {
            const leftMass = Math.max(1, leftRadius * leftRadius)
            const rightMass = Math.max(1, rightRadius * rightRadius)
            leftShare = rightMass / (leftMass + rightMass)
            rightShare = leftMass / (leftMass + rightMass)
          }
          const relativeNormalVelocity = ((right.vx ?? 0) - (left.vx ?? 0)) * unitX
            + ((right.vy ?? 0) - (left.vy ?? 0)) * unitY
          // Resolve penetration with a small position correction. Velocity is
          // used only to cancel inward motion; it is never reflected outward.
          const positionCorrection = Math.min(12, penetration * 0.5) * strength
          left.x = (left.x ?? 0) - unitX * positionCorrection * leftShare
          left.y = (left.y ?? 0) - unitY * positionCorrection * leftShare
          right.x = (right.x ?? 0) + unitX * positionCorrection * rightShare
          right.y = (right.y ?? 0) + unitY * positionCorrection * rightShare
          const stopClosing = Math.max(0, -relativeNormalVelocity)
          const normalAdjustment = stopClosing
          const pushX = unitX * normalAdjustment
          const pushY = unitY * normalAdjustment
          left.vx = (left.vx ?? 0) - pushX * leftShare
          left.vy = (left.vy ?? 0) - pushY * leftShare
          right.vx = (right.vx ?? 0) + pushX * rightShare
          right.vy = (right.vy ?? 0) + pushY * rightShare
        }
      }
    }
  }) as unknown as DynamicCollisionForce

  force.initialize = (nextNodes) => {
    nodes = nextNodes
  }
  force.effectiveRadius = effectiveRadius
  return force
}
